import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Truck, Wrench, X } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'transport', label: 'Transport', color: '#1565C0' },
  { value: 'installation', label: 'Installation', color: '#F59E0B' },
  { value: 'reparation', label: 'Reparation', color: '#C62828' },
  { value: 'consommable', label: 'Consommable', color: '#7C3AED' },
  { value: 'autre', label: 'Autre', color: '#5E5F66' },
];

const monthsBetween = (start, end) => {
  if (!start) return 0;
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    return Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth())) + 1;
  } catch { return 0; }
};

const computeRentalTotal = (r) => {
  const months = r.months > 0 ? r.months : monthsBetween(r.start_date, r.end_date);
  const rentalCost = (r.monthly_cost || 0) * months;
  const expensesTotal = (r.expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  return { months, rentalCost, expensesTotal, total: rentalCost + expensesTotal };
};

export const ProjectRentalsSection = ({ project, onChange }) => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', rental_company: '', monthly_cost: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', months: '', notes: '' });
  const [expForm, setExpForm] = useState({ rentalId: null, date: new Date().toISOString().slice(0, 10), amount: '', category: 'transport', description: '' });

  const fetchData = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/projects/${project.id}/rentals`, { withCredentials: true });
      setRentals(r.data || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [project?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setForm({ label: '', rental_company: '', monthly_cost: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', months: '', notes: '' }); setEditing(null); };

  const saveRental = async () => {
    if (!form.label.trim()) return toast.error('Libelle requis');
    if (!form.monthly_cost) return toast.error('Loyer mensuel requis');
    const payload = { ...form, monthly_cost: parseFloat(form.monthly_cost) || 0, months: parseFloat(form.months) || 0 };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/projects/${project.id}/rentals/${editing.id}`, payload, { withCredentials: true });
        toast.success('Location modifiee');
      } else {
        await axios.post(`${API_URL}/api/projects/${project.id}/rentals`, payload, { withCredentials: true });
        toast.success('Location ajoutee');
      }
      setShowForm(false); resetForm(); fetchData(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const editRental = (r) => {
    setEditing(r);
    setForm({ label: r.label, rental_company: r.rental_company || '', monthly_cost: r.monthly_cost || '', start_date: r.start_date || '', end_date: r.end_date || '', months: r.months || '', notes: r.notes || '' });
    setShowForm(true);
  };

  const deleteRental = async (id) => {
    if (!window.confirm('Supprimer cette location et ses depenses liees ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/rentals/${id}`, { withCredentials: true });
      toast.success('Supprimee'); fetchData(); onChange?.();
    } catch { toast.error('Erreur'); }
  };

  const addExpense = async (rentalId) => {
    if (!expForm.amount || parseFloat(expForm.amount) <= 0) return toast.error('Montant requis');
    try {
      await axios.post(`${API_URL}/api/projects/${project.id}/rentals/${rentalId}/expenses`, { ...expForm, amount: parseFloat(expForm.amount) }, { withCredentials: true });
      toast.success('Depense ajoutee');
      setExpForm({ rentalId: null, date: new Date().toISOString().slice(0, 10), amount: '', category: 'transport', description: '' });
      fetchData(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const deleteExpense = async (rentalId, expId) => {
    if (!window.confirm('Supprimer cette depense ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/rentals/${rentalId}/expenses/${expId}`, { withCredentials: true });
      toast.success('Supprimee'); fetchData(); onChange?.();
    } catch { toast.error('Erreur'); }
  };

  const grandTotal = rentals.reduce((s, r) => s + computeRentalTotal(r).total, 0);

  return (
    <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #7C3AED' }} data-testid="project-rentals-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#5B21B6', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Truck size={18} weight="bold" /> Locations machines et equipement ({rentals.length})
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.82rem', color: '#5B21B6', fontWeight: 700 }}>
            Total : {grandTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR HT
          </span>
          <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="rental-add-btn" style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
            <Plus size={14} /> Ajouter
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 12, fontSize: '0.85rem', color: '#5E5F66' }}>Chargement…</div>
      ) : rentals.length === 0 ? (
        <div style={{ padding: 12, fontSize: '0.85rem', color: '#5E5F66' }}>Aucune location enregistree. Cliquez sur Ajouter pour creer la premiere.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rentals.map(r => {
            const { months, rentalCost, expensesTotal, total } = computeRentalTotal(r);
            return (
              <div key={r.id} data-testid={`rental-row-${r.id}`} style={{ padding: 12, background: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ minWidth: 0, flex: '1 1 250px' }}>
                    <div style={{ fontWeight: 700, color: '#1A1A1E' }}>{r.label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: 2 }}>
                      {r.rental_company || 'Loueur ?'} · {r.monthly_cost?.toLocaleString('fr-FR') || 0} EUR/mois × {months} mois
                      {r.start_date && ` · ${fmtDateFR(r.start_date)}${r.end_date ? ' → ' + fmtDateFR(r.end_date) : ''}`}
                    </div>
                    {r.notes && <div style={{ fontSize: '0.75rem', color: '#7E7F85', marginTop: 2, fontStyle: 'italic' }}>{r.notes}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#5B21B6' }}>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR</div>
                    <div style={{ fontSize: '0.7rem', color: '#5E5F66' }}>
                      Loyer {rentalCost.toLocaleString('fr-FR')} + Depenses {expensesTotal.toLocaleString('fr-FR')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => editRental(r)} data-testid={`rental-edit-${r.id}`}><PencilSimple size={13} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteRental(r.id)} data-testid={`rental-delete-${r.id}`}><Trash size={13} /></button>
                  </div>
                </div>

                {/* Expenses list */}
                {(r.expenses || []).length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #DDD6FE' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#5B21B6', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                      Depenses liees ({r.expenses.length})
                    </div>
                    {r.expenses.map(e => {
                      const cat = CATEGORIES.find(c => c.value === e.category) || CATEGORIES[CATEGORIES.length - 1];
                      return (
                        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.82rem' }}>
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', background: `${cat.color}15`, color: cat.color, borderRadius: 999, fontWeight: 700 }}>{cat.label}</span>
                          <span style={{ color: '#5E5F66' }}>{fmtDateFR(e.date)}</span>
                          <span style={{ flex: 1 }}>{e.description || '-'}</span>
                          <span style={{ fontWeight: 700 }}>{Number(e.amount).toLocaleString('fr-FR')} EUR</span>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteExpense(r.id, e.id)} data-testid={`rental-exp-del-${e.id}`}><Trash size={12} /></button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add expense form (inline) */}
                {expForm.rentalId === r.id ? (
                  <div style={{ marginTop: 10, padding: 8, background: 'white', border: '1px solid #DDD6FE', borderRadius: 6, display: 'grid', gridTemplateColumns: '110px 110px 130px 1fr auto auto', gap: 6, alignItems: 'end' }}>
                    <div><label style={{ fontSize: '0.7rem', fontWeight: 700 }}>Date</label><input type="date" className="form-input" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} data-testid="rental-exp-date" /></div>
                    <div><label style={{ fontSize: '0.7rem', fontWeight: 700 }}>Montant</label><input type="number" step="0.01" className="form-input" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} placeholder="0.00" data-testid="rental-exp-amount" /></div>
                    <div><label style={{ fontSize: '0.7rem', fontWeight: 700 }}>Categorie</label>
                      <select className="form-input" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} data-testid="rental-exp-category">
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div><label style={{ fontSize: '0.7rem', fontWeight: 700 }}>Description</label><input type="text" className="form-input" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} placeholder="Optionnel" data-testid="rental-exp-desc" /></div>
                    <button className="btn btn-primary btn-sm" onClick={() => addExpense(r.id)} data-testid={`rental-exp-save-${r.id}`}>OK</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setExpForm({ rentalId: null, date: new Date().toISOString().slice(0, 10), amount: '', category: 'transport', description: '' })}><X size={13} /></button>
                  </div>
                ) : (
                  <button className="btn btn-secondary btn-sm" onClick={() => setExpForm({ ...expForm, rentalId: r.id })} data-testid={`rental-exp-add-${r.id}`} style={{ marginTop: 8 }}>
                    <Wrench size={13} /> Ajouter une depense
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal form for create / edit */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 10, padding: 20, width: '100%', maxWidth: 520 }} data-testid="rental-form-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>{editing ? 'Modifier la location' : 'Nouvelle location'}</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
            </div>
            <div className="form-group"><label className="form-label">Libelle *</label><input type="text" className="form-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex: Nacelle 12m, Mini-pelle..." data-testid="rental-form-label" /></div>
            <div className="form-group"><label className="form-label">Loueur</label><input type="text" className="form-input" value={form.rental_company} onChange={(e) => setForm({ ...form, rental_company: e.target.value })} placeholder="Loxam, Kiloutou..." data-testid="rental-form-company" /></div>
            <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label className="form-label">Loyer mensuel HT *</label><input type="number" step="0.01" className="form-input" value={form.monthly_cost} onChange={(e) => setForm({ ...form, monthly_cost: e.target.value })} placeholder="0.00" data-testid="rental-form-cost" /></div>
              <div><label className="form-label">Nb mois (forcer)</label><input type="number" step="1" min="0" className="form-input" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} placeholder="Auto si 0" data-testid="rental-form-months" /></div>
            </div>
            <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label className="form-label">Date debut</label><input type="date" className="form-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rental-form-start" /></div>
              <div><label className="form-label">Date fin</label><input type="date" className="form-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="rental-form-end" /></div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" rows={2} data-testid="rental-form-notes" /></div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); resetForm(); }} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveRental} style={{ flex: 1, background: '#7C3AED', borderColor: '#7C3AED' }} data-testid="rental-form-save">{editing ? 'Enregistrer' : 'Creer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectRentalsSection;
